import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Address } from '@liberty-financial/agp-shared';
import {
  dateValidator,
  dateBeforeValidator,
  dateAfterValidator
} from '@liberty-financial/ng-core';
import { diff } from 'deep-object-diff';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  Company,
  ContactSummary,
  ContactType,
  ContactReferrerArrangement,
  Shareholder
} from '@features/contacts/models';
import { CompanyService } from '@features/contacts/services/company.service';
import { TagEntityType } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';
import {
  commandEvent,
  CommandEvent
} from '@shared/components/load/command/command.component';
import { AU_GST_COMMENCED } from '@shared/constants';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';

export interface SchemaDefinition {
  company: Company.SchemaPropertyDefinition;
  shareholder: Shareholder.SchemaPropertyDefinition;
}

export interface Content {
  company: Company.Model;
  contacts: ContactSummary[];
}

export type LoadContent = {
  schema: SchemaDefinition;
  content: Content;
};

@Component({ template: '' })
export abstract class CompanyDetailsComponent implements OnInit {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  companyId!: string;
  tagEntityType: TagEntityType = TagEntityType.Companies;
  pendingTags: Tag[] = [];

  private loadSchema$: Observable<SchemaDefinition> = combineLatest([
    this.companyService.getCompanyDefinition(),
    this.companyService.getShareholderDefinition()
  ]).pipe(map(([company, shareholder]) => ({ company, shareholder })));

  public load$!: Observable<LoadContent>;
  public load!: LoadContent;
  protected change$: Subject<Partial<Company.Model>> = new Subject<
    Partial<Company.Model>
  >();
  public update$: Observable<CommandEvent<object>> = commandEvent(
    this.change$,
    (x) => this.companyService.updateCompany(this.companyId, x)
  );

  formGroup = new FormGroup({
    name: new FormControl<string>('', [Validators.maxLength(160)]),
    tradingName: new FormControl<string | null>(null, [
      Validators.maxLength(150)
    ]),
    businessNumber: new FormControl<string | null>(null, []),
    companyNumber: new FormControl<string | null>(null, []),
    startDate: new FormControl<string | null>(null, [
      dateValidator(),
      dateBeforeValidator()
    ]),
    gstRegistrationDate: new FormControl<string | null>(null, [
      dateValidator(),
      dateBeforeValidator(),
      dateAfterValidator(AU_GST_COMMENCED)
    ]),
    businessNature: new FormControl<string | null>(null),
    accountantName: new FormControl<string | null>(null, [
      Validators.maxLength(160)
    ]),
    accountantPhone: new FormControl<string | null>(null),
    address: new FormControl<Address | null>(null),
    registeredOffice: new FormControl<Address | null>(null),
    directors: new FormControl<string[]>([]),
    shareholders: new FormControl<Shareholder.Model[]>([]),
    contactType: new FormControl<ContactType | null>(null),
    status: new FormControl<string | null>(null),
    nmbAffiliateId: new FormControl<string | null>(null),
    referrerArrangement:
      new FormControl<ContactReferrerArrangement.Model | null>(null)
  });

  constructor(protected companyService: CompanyService) {}

  startDateLabel = $localize`:Start Date Label|Country specific business start date title@@start-date:Date started`;

  ngOnInit(): void {
    this.load$ = combineLatest([this.loadSchema$, this.content()]).pipe(
      map(([schema, content]) => ({ schema, content })),
      tap((x) => {
        StandardSchemaUtil.attachFormGroupToSchema(
          this.formGroup,
          x.schema.company
        );
        this.formGroup.patchValue(x.content.company);
        this.load = x;
      })
    );
  }

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract readonly isNewEntity: boolean;
  abstract content(): Observable<Content>;

  onTagsChanged(tags: Tag[]): void {
    this.pendingTags = tags;
  }

  save() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }
    
    const formValue = this.formGroup.getRawValue();
    
    // Convert empty string to null for businessNumber
    if (formValue.businessNumber === '') {
      formValue.businessNumber = null;
    }
    
    // Convert empty string to null for companyNumber
    if (formValue.companyNumber === '') {
      formValue.companyNumber = null;
    }
    
    // Clear referrerArrangement if contact type is not Referrer
    if (formValue.contactType !== ContactType.Referrer) {
      formValue.referrerArrangement = null;
    }
    
    let delta = diff(
      this.load.content.company,
      formValue
    ) as Partial<Company.Model>;
    if (delta.directors) {
      delta.directors = formValue.directors || undefined;
    }

    if (delta.shareholders) {
      delta.shareholders = formValue.shareholders || undefined;
    }

    if (delta.referrerArrangement) {
      delta.referrerArrangement =
        (formValue.referrerArrangement as ContactReferrerArrangement.Model) ||
        null;
    }

    // Fix: If address changed, send the complete address object, not just the diff
    if (delta.address) {
      delta.address = formValue.address || undefined;
    }

    // Fix: If registeredOffice changed, send the complete address object, not just the diff
    if (delta.registeredOffice) {
      delta.registeredOffice = formValue.registeredOffice || undefined;
    }

    this.change$.next(delta);
  }
}
